import React, { useState, useContext } from "react";
import { AuthContext } from '../../AuthContext';
import { generateSlug } from "../../utils/slug";
import Swal from "sweetalert2";
import "./AdDetailsAdAction.css";

const AdDetailsAdAction = ({ adId, adTitle }) => {
    const { user } = useContext(AuthContext);
    const [isFavorite, setIsFavorite] = useState(false); // Track if the item is favorited

    // Function to handle favorite click
    // This function will send a POST request to add the item to the user's favorites
    const handleFavoriteClick = async () => {
        try {
            const userId = user?.id;

            if (!userId) {
                Swal.fire("Niste prijavljeni.", "Za to dejanje se prosimo prijavite v svoj račun.", "warning");;
                return;
            }

            if (!adId) {
                Swal.fire("Neveljaven oglas", "Oglas ni bil najden.", "error");;
                return;
            }

            // Send POST request to add to favorites
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/user/${userId}/favorites/${adId}`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: {
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.ok) {
                setIsFavorite(true);
                Swal.fire("Uspeh!", "Oglas je bil uspešno shranjen.", "success");
            } else if (response.status === 401) {
                Swal.fire("Niste prijavljeni.", "Za to dejanje se prosimo prijavite v svoj račun.", "warning");
            } else {
                const errorData = await response.json();
                Swal.fire("Napaka", errorData.message || "Oglasa ni bilo mogoče shraniti.", "error");
            }
        } catch (error) {
            console.error("Error adding to favorites:", error);
            Swal.fire("Napaka", "Prišlo je do napake. Poskusite znova.", "error");
        }
    };

    // Function to handle share click
    // This function will open a modal with share options for Facebook, WhatsApp, and email
    const handleShareClick = () => {
        const shareUrl = window.location.href;
        const shareText = encodeURIComponent(`Poglej ta oglas: ${adTitle}\n${shareUrl}`);
        const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
        const whatsappUrl = `https://api.whatsapp.com/send?text=${shareText}`;
        const mailtoUrl = `mailto:?subject=${encodeURIComponent('Oglas: ' + adTitle)}&body=${shareText}`;

        Swal.fire({
            title: 'Deli oglas',
            html: `
        <div class="share-buttons">
          <a href="${facebookUrl}" target="_blank" rel="noopener noreferrer" class="share-button fb">
            <i class="fab fa-facebook-f"></i> Facebook
          </a>
          <a href="${whatsappUrl}" target="_blank" rel="noopener noreferrer" class="share-button wa">
            <i class="fab fa-whatsapp"></i> WhatsApp
          </a>
          <a href="${mailtoUrl}" class="share-button mail">
            <i class="fa fa-envelope"></i> E-pošta
          </a>
        </div>
      `,
            showCloseButton: true,
            showConfirmButton: false,
            customClass: {
                popup: 'share-popup',
                content: 'share-content'
            }
        });
    };

    // Function to handle report click
    // This function will prompt the user for a reason and send a report request
    const handleReportClick = async () => {
        const userId = user?.id;

        if (!userId) {
            return Swal.fire("Niste prijavljeni.", "Za to dejanje se prosimo prijavite v svoj račun.", "warning");
        }

        const { value: reason } = await Swal.fire({
            title: 'Prijavi oglas',
            input: 'textarea',
            inputLabel: 'Razlog prijave',
            inputPlaceholder: 'Vnesi razlog...',
            showCancelButton: true
        });

        // Preverimo, ali je uporabnik vnesel razlog in kliknil "Prijavi"
        if (reason) {
            try {
                const slug = generateSlug(adTitle);
                const reportUrl = `${process.env.REACT_APP_API_URL}/oglas/${slug}/${adId}/report`;
                const response = await fetch(
                    reportUrl,
                    {
                        method: 'POST',
                        credentials: 'include',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ reason }),
                    }
                );

                // Če je zahtevek uspešen (status 2xx)
                if (response.ok) {
                    Swal.fire('Uspeh', 'Oglas je bil uspešno prijavljen. Hvala za povratno informacijo.', 'success');
                } else {
                    // Preberemo telo odgovora, da dobimo sporočilo o napaki iz strežnika
                    const errorData = await response.json();

                    // Preverimo, ali gre za specifično napako "že prijavljeno" (status 409)
                    if (response.status === 409) {
                        Swal.fire('Opozorilo', errorData.message || 'Ta oglas ste že prijavili.', 'warning');
                    } else {
                        // Za vse druge napake (npr. 500, 404) prikažemo splošno sporočilo
                        Swal.fire('Napaka', errorData.message || 'Pri prijavi oglasa je prišlo do težave.', 'error');
                    }
                }
            } catch (err) {
                console.error("Error reporting listing:", err);
                Swal.fire('Napaka', 'Prišlo je do napake v omrežju. Poskusite znova.', 'error');
            }
        } else if (reason === '') {
            // Če uporabnik klikne "Prijavi", ampak ne vnese razloga
            Swal.fire('Opozorilo', 'Prosimo, vnesite razlog za prijavo.', 'info');
        }
    };

    return (
        <div className="item_action">
            <h5 className="item_action_title">⁍ Akcije</h5>
            <div className="item_action_btns d-flex justify-content-between align-items-center">
                <button className="item_action_button" onClick={handleShareClick}>
                    <i className="fa-regular fa-share-from-square"></i>
                    <span>Deli</span>
                </button>
                <button
                    className={`item_action_button ${isFavorite ? "favorited" : ""}`}
                    onClick={handleFavoriteClick} // Handle click to save to favorites
                >
                    <i className={`fa-regular fa-heart ${isFavorite ? "favorited" : ""}`}></i>
                    <span>{isFavorite ? "Shranjeno" : "Shrani"}</span>
                </button>
                <button className="item_action_button" onClick={handleReportClick}>
                    <i className="fa-regular fa-flag"></i>
                    <span>Prijavi</span>
                </button>
            </div>
        </div>
    );
};

export default AdDetailsAdAction;